import { create } from "zustand";
import { persist, type PersistStorage, type StorageValue } from "zustand/middleware";

export type PlayableKind = "song" | "video" | "episode";

/** One playable item plus enough context to render "Playing from X" and drive `hls.js`. */
export interface PlayerTrack {
  kind: PlayableKind;
  id: number;
  title: string;
  /** Artist names (or the show name) shown under the title. */
  subtitle?: string;
  /** Credited artists — the title area links to them. Main artist first. */
  artists?: { id: number; name: string; code?: string }[];
  /** Public page key. */
  code?: string;
  /** The song a video is the clip of (video ↔ song link), or the album a song belongs to is not tracked. */
  relatedSongId?: number | null;
  cover: string | null;
  /** The `GET /songs/:code/stream` or `/videos/:code/stream` HLS manifest URL. */
  streamUrl: string;
  durationSeconds: number;
  /** Resume point (seconds) applied once the track loads — podcast episodes. */
  startAt?: number;
  /** Queued by autoplay (related songs after the queue ran out), not by the listener. */
  autoAdded?: boolean;
  /** "Add to queue": plays right after what's playing, ahead of the rest of the list (Spotify's user queue). */
  queuedByUser?: boolean;
}

export interface PlayerContext {
  /** What queued this track — shown in the mini-player as "Playing from …". */
  label: string;
  href?: string;
}

export type RepeatMode = "off" | "all" | "one";

/** Which view the now-playing side panel (and full-screen player) shows. */
export type PlayerPanel = "queue" | "lyrics";

/** How the current video sits in its page: normal, or wide ("theater"). Fullscreen is `isFullscreen`. */
export type VideoMode = "inline" | "theater";

interface PlayerState {
  queue: PlayerTrack[];
  /** The queue as it was before shuffling — restored when shuffle turns off. */
  unshuffledQueue: PlayerTrack[] | null;
  currentIndex: number;
  context: PlayerContext | null;
  isPlaying: boolean;
  /** Current position, seconds — the source of truth for the progress bar (always renders left-to-right, see `packages/ui`'s player-progress component). */
  positionSeconds: number;
  /**
   * A seek the `<audio>` element must perform — `nonce` changes on every
   * request so seeking to the same second twice still fires.
   */
  seekRequest: { seconds: number; nonce: number } | null;
  volume: number;
  muted: boolean;
  shuffle: boolean;
  repeat: RepeatMode;
  /** Spotify's autoplay: when the queue runs out, keep going with similar songs. */
  autoplay: boolean;
  isExpanded: boolean;
  /** The video's stage size — the page and the fullscreen layer read this. */
  videoMode: VideoMode;
  /** True while the now-playing view is in the browser's fullscreen mode. */
  isFullscreen: boolean;
  /** Where the next video page opened says it came from ("Trending Videos") — becomes `context.label`. */
  videoContextHint: PlayerContext | null;
  /** Desktop "now playing / queue" side panel. */
  isQueueOpen: boolean;
  panel: PlayerPanel;
  /**
   * Where a reload left the restored track (`key` = `kind-id`) — the bar
   * seeks there once that track's manifest loads (`resumePoint`), then clears it.
   */
  resumeAt: { key: string; seconds: number } | null;

  current: () => PlayerTrack | undefined;
  playQueue: (tracks: PlayerTrack[], startIndex: number, context: PlayerContext) => void;
  playSingle: (track: PlayerTrack, context: PlayerContext) => void;
  /** Adds tracks after the queue's end (autoplay). */
  appendToQueue: (tracks: PlayerTrack[]) => void;
  /** "Add to queue": next up, after any other track the listener queued; starts playback when nothing is playing. */
  addToQueue: (track: PlayerTrack, context?: PlayerContext) => void;
  togglePlay: () => void;
  pause: () => void;
  play: () => void;
  /** Skip button, and the end of a track (`auto`) — repeat and the queue end apply. */
  next: (auto?: boolean) => void;
  /** Spotify: after 3 seconds "previous" restarts the track instead. */
  previous: () => void;
  seekTo: (seconds: number) => void;
  setPosition: (seconds: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  toggleShuffle: () => void;
  cycleRepeat: () => void;
  setAutoplay: (on: boolean) => void;
  setExpanded: (expanded: boolean) => void;
  setVideoMode: (mode: VideoMode) => void;
  setFullscreen: (on: boolean) => void;
  setVideoContextHint: (hint: PlayerContext | null) => void;
  /** Jump to a queued track (the queue panel). */
  playAt: (index: number) => void;
  toggleQueue: () => void;
  setQueueOpen: (open: boolean) => void;
  /** Opens the side panel on a view, or closes it when that view is already showing. */
  togglePanel: (panel: PlayerPanel) => void;
  clear: () => void;
  /** The restored resume point for `track` — `null` when there is none (or it's another track's). */
  resumePoint: (track: PlayerTrack) => number | null;
  /** Drops the restored resume point — the first track that loads after a reload used it (or replaced it). */
  clearResume: () => void;
}

/** `kind-id` — what a saved position is keyed on. */
export const trackKey = (track: Pick<PlayerTrack, "kind" | "id">) => `${track.kind}-${track.id}`;

function shuffled<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}

const sameTrack = (a: PlayerTrack, b: PlayerTrack) => a.kind === b.kind && a.id === b.id;

/**
 * Global client-side player state (product plan's web player requirement) —
 * a Zustand store, not a page's component tree, so playback survives
 * client-side navigation across the whole site. `packages/ui`'s
 * `<PlayerBar>` (rendered once, in the root layout) is the only place that
 * touches `hls.js`/`<audio>` directly; every "Play" button anywhere on the
 * site just calls `playQueue`/`playSingle`.
 */
export const usePlayerStore = create<PlayerState>()(
  persist(
    (set, get) => ({
      queue: [],
      unshuffledQueue: null,
      currentIndex: -1,
      context: null,
      isPlaying: false,
      positionSeconds: 0,
      seekRequest: null,
      volume: 1,
      muted: false,
      shuffle: false,
      repeat: "off",
      autoplay: true,
      isExpanded: false,
      videoMode: "inline",
      isFullscreen: false,
      videoContextHint: null,
      isQueueOpen: false,
      panel: "queue",
      resumeAt: null,

      current: () => {
        const { queue, currentIndex } = get();
        return currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : undefined;
      },

      playQueue: (tracks, startIndex, context) => {
        const start = Math.min(Math.max(startIndex, 0), Math.max(tracks.length - 1, 0));
        // With shuffle on, the tapped track plays first and the rest is shuffled.
        if (get().shuffle && tracks.length > 1) {
          const first = tracks[start]!;
          set({
            queue: [first, ...shuffled(tracks.filter((_, i) => i !== start))],
            unshuffledQueue: tracks,
            currentIndex: 0,
            context,
            isPlaying: true,
            positionSeconds: 0,
          });
          return;
        }
        set({
          queue: tracks,
          unshuffledQueue: null,
          currentIndex: start,
          context,
          isPlaying: true,
          positionSeconds: 0,
        });
      },

      playSingle: (track, context) =>
        set({
          queue: [track],
          unshuffledQueue: null,
          currentIndex: 0,
          context,
          isPlaying: true,
          positionSeconds: 0,
        }),

      appendToQueue: (tracks) =>
        set((state) => {
          const fresh = tracks.filter((t) => !state.queue.some((q) => sameTrack(q, t)));
          if (fresh.length === 0) return {};
          return {
            queue: [...state.queue, ...fresh],
            unshuffledQueue: state.unshuffledQueue ? [...state.unshuffledQueue, ...fresh] : null,
          };
        }),

      addToQueue: (track, context) =>
        set((state) => {
          if (state.queue.length === 0 || state.currentIndex < 0) {
            return {
              queue: [track],
              unshuffledQueue: null,
              currentIndex: 0,
              context: context ?? { label: "صف پخش" },
              isPlaying: true,
              positionSeconds: 0,
            };
          }
          const queued = { ...track, queuedByUser: true };
          // After the playing track and the tracks the listener already queued.
          let at = state.currentIndex + 1;
          while (at < state.queue.length && state.queue[at]!.queuedByUser) at++;
          const queue = [...state.queue.slice(0, at), queued, ...state.queue.slice(at)];
          return {
            queue,
            unshuffledQueue: state.unshuffledQueue ? [...state.unshuffledQueue, queued] : null,
          };
        }),

      togglePlay: () => set((state) => ({ isPlaying: state.queue.length > 0 && !state.isPlaying })),
      pause: () => set({ isPlaying: false }),
      play: () => set((state) => ({ isPlaying: state.queue.length > 0 || state.isPlaying })),

      next: (auto = false) =>
        set((state) => {
          if (auto && state.repeat === "one") {
            return {
              positionSeconds: 0,
              seekRequest: { seconds: 0, nonce: Date.now() },
              isPlaying: true,
            };
          }
          if (state.currentIndex + 1 < state.queue.length) {
            return { currentIndex: state.currentIndex + 1, positionSeconds: 0, isPlaying: true };
          }
          if (state.repeat === "all" && state.queue.length > 0) {
            // A one-track queue "wraps" onto itself — restart it in place.
            if (state.queue.length === 1) {
              return {
                positionSeconds: 0,
                seekRequest: { seconds: 0, nonce: Date.now() },
                isPlaying: true,
              };
            }
            return { currentIndex: 0, positionSeconds: 0, isPlaying: true };
          }
          return auto ? { isPlaying: false } : {};
        }),

      previous: () =>
        set((state) => {
          if (state.positionSeconds > 3 || state.currentIndex <= 0) {
            return { positionSeconds: 0, seekRequest: { seconds: 0, nonce: Date.now() } };
          }
          return { currentIndex: state.currentIndex - 1, positionSeconds: 0, isPlaying: true };
        }),

      seekTo: (seconds) => {
        const clamped = Math.max(0, seconds);
        set({ positionSeconds: clamped, seekRequest: { seconds: clamped, nonce: Date.now() } });
      },
      setPosition: (seconds) => set({ positionSeconds: seconds }),
      setVolume: (volume) => set({ volume: Math.min(1, Math.max(0, volume)), muted: false }),
      toggleMute: () => set((state) => ({ muted: !state.muted })),

      toggleShuffle: () =>
        set((state) => {
          const current = state.queue[state.currentIndex];
          if (state.shuffle) {
            // Back to the original order, keeping the playing track.
            const original = state.unshuffledQueue ?? state.queue;
            const index = current ? original.findIndex((t) => sameTrack(t, current)) : -1;
            return {
              shuffle: false,
              queue: original,
              unshuffledQueue: null,
              currentIndex: index >= 0 ? index : state.currentIndex,
            };
          }
          if (!current || state.queue.length < 2) return { shuffle: true };
          // Spotify: what's playing stays; everything else is reshuffled after it.
          const rest = state.queue.filter((_, i) => i !== state.currentIndex);
          return {
            shuffle: true,
            unshuffledQueue: state.queue,
            queue: [current, ...shuffled(rest)],
            currentIndex: 0,
          };
        }),

      cycleRepeat: () =>
        set((state) => ({
          repeat: state.repeat === "off" ? "all" : state.repeat === "all" ? "one" : "off",
        })),

      setAutoplay: (on) => set({ autoplay: on }),
      setExpanded: (expanded) => set({ isExpanded: expanded }),
      setVideoMode: (videoMode) => set({ videoMode }),
      setFullscreen: (isFullscreen) => set({ isFullscreen }),
      setVideoContextHint: (videoContextHint) => set({ videoContextHint }),
      playAt: (index) =>
        set((state) =>
          index >= 0 && index < state.queue.length
            ? { currentIndex: index, positionSeconds: 0, isPlaying: true }
            : {},
        ),
      toggleQueue: () =>
        set((state) =>
          state.isQueueOpen && state.panel === "queue"
            ? { isQueueOpen: false }
            : { isQueueOpen: true, panel: "queue" },
        ),
      setQueueOpen: (open) => set({ isQueueOpen: open }),
      togglePanel: (panel) =>
        set((state) =>
          state.isQueueOpen && state.panel === panel
            ? { isQueueOpen: false }
            : { isQueueOpen: true, panel },
        ),
      clear: () =>
        set({
          queue: [],
          unshuffledQueue: null,
          currentIndex: -1,
          context: null,
          isPlaying: false,
          positionSeconds: 0,
          resumeAt: null,
        }),
      resumePoint: (track) => {
        const resume = get().resumeAt;
        return resume && resume.key === trackKey(track) ? resume.seconds : null;
      },
      clearResume: () => {
        if (get().resumeAt) set({ resumeAt: null });
      },
    }),
    {
      name: "gerama.player",
      version: 1,
      storage: throttledLocalStorage<PersistedPlayer>(),
      partialize: (state): PersistedPlayer => {
        const current = state.queue[state.currentIndex];
        return {
          queue: state.queue,
          unshuffledQueue: state.unshuffledQueue,
          currentIndex: state.currentIndex,
          context: state.context,
          isExpanded: state.isExpanded,
          panel: state.panel,
          volume: state.volume,
          muted: state.muted,
          shuffle: state.shuffle,
          repeat: state.repeat,
          resume: current
            ? { key: trackKey(current), seconds: Math.floor(state.positionSeconds) }
            : null,
        };
      },
      merge: (persisted, current) => restorePlayer(persisted, current),
    },
  ),
);

/** What survives a reload: the queue and where it was — never "playing" (browsers block autoplay). */
interface PersistedPlayer {
  queue: PlayerTrack[];
  unshuffledQueue: PlayerTrack[] | null;
  currentIndex: number;
  context: PlayerContext | null;
  isExpanded: boolean;
  panel: PlayerPanel;
  volume: number;
  muted: boolean;
  shuffle: boolean;
  repeat: RepeatMode;
  resume: { key: string; seconds: number } | null;
}

/**
 * A reload brings back the same track at the same second, paused and ready to
 * resume. The saved second is keyed on the track it was saved for, so a stale
 * position never lands on another track; a track that had (nearly) ended
 * starts over.
 */
function restorePlayer(persisted: unknown, current: PlayerState): PlayerState {
  const saved = persisted as Partial<PersistedPlayer> | undefined;
  if (!saved || !Array.isArray(saved.queue) || saved.queue.length === 0) return current;
  const valid = saved.queue.every(
    (t) => !!t && typeof t.streamUrl === "string" && typeof t.id === "number",
  );
  const index = typeof saved.currentIndex === "number" ? saved.currentIndex : -1;
  if (!valid || index < 0 || index >= saved.queue.length) return current;
  const track = saved.queue[index]!;
  const resume =
    saved.resume &&
    saved.resume.key === trackKey(track) &&
    saved.resume.seconds > 1 &&
    !(track.durationSeconds > 0 && saved.resume.seconds >= track.durationSeconds - 3)
      ? saved.resume
      : null;
  return {
    ...current,
    queue: saved.queue,
    unshuffledQueue: Array.isArray(saved.unshuffledQueue) ? saved.unshuffledQueue : null,
    currentIndex: index,
    context: saved.context ?? null,
    isExpanded: !!saved.isExpanded,
    panel: saved.panel === "lyrics" ? "lyrics" : "queue",
    volume: typeof saved.volume === "number" ? Math.min(1, Math.max(0, saved.volume)) : 1,
    muted: !!saved.muted,
    shuffle: !!saved.shuffle,
    repeat: saved.repeat === "all" || saved.repeat === "one" ? saved.repeat : "off",
    isPlaying: false,
    positionSeconds: resume?.seconds ?? 0,
    resumeAt: resume,
  };
}

/** Writes at most this often while playing — the position ticks several times a second. */
const WRITE_INTERVAL_MS = 3000;

/**
 * `localStorage`, throttled: every store update (each `timeupdate` too) asks
 * to save, and only the latest snapshot is written, at most every few seconds
 * — plus straight away when the tab is hidden or closed, so a reload loses
 * nothing. `undefined` on the server and when storage is blocked (persist then
 * just keeps state in memory).
 */
function throttledLocalStorage<S>(): PersistStorage<S> | undefined {
  if (typeof window === "undefined") return undefined;
  let storage: Storage;
  try {
    storage = window.localStorage;
  } catch {
    return undefined;
  }
  let pending: { name: string; value: StorageValue<S> } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastWrite = 0;

  const flush = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (!pending) return;
    const { name, value } = pending;
    pending = null;
    lastWrite = Date.now();
    try {
      storage.setItem(name, JSON.stringify(value));
    } catch {
      // Quota / blocked — the next reload just starts empty.
    }
  };
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });

  return {
    getItem: (name) => {
      try {
        const raw = storage.getItem(name);
        return raw ? (JSON.parse(raw) as StorageValue<S>) : null;
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      pending = { name, value };
      const wait = WRITE_INTERVAL_MS - (Date.now() - lastWrite);
      if (wait <= 0) flush();
      else timer ??= setTimeout(flush, wait);
    },
    removeItem: (name) => {
      pending = null;
      try {
        storage.removeItem(name);
      } catch {
        // Blocked.
      }
    },
  };
}
